import { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, Line, Text } from 'react-konva';
import Konva from 'konva';
import { MapPin } from 'lucide-react';
import type { GymFloorPlan } from '@/types';

interface MachinePositionPickerProps {
  floorPlans: GymFloorPlan[];
  selectedFloorPlanId: string | null;
  position: { x: number; y: number } | null;
  onFloorPlanChange: (floorPlanId: string | null) => void;
  onPositionChange: (position: { x: number; y: number } | null) => void;
}

export const MachinePositionPicker = ({
  floorPlans,
  selectedFloorPlanId,
  position,
  onFloorPlanChange,
  onPositionChange,
}: MachinePositionPickerProps) => {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 300 });

  const selectedFloorPlan = floorPlans.find(fp => fp.id === selectedFloorPlanId);

  // Responsive canvas size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = Math.min(containerRef.current.clientWidth, 500);
        const height = Math.round(width * 0.75);
        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [selectedFloorPlanId]);

  const handleStageClick = (_e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!selectedFloorPlan) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    // Scale position relative to original floor plan size
    const scaleX = selectedFloorPlan.drawingData.width / canvasSize.width;
    const scaleY = selectedFloorPlan.drawingData.height / canvasSize.height;

    onPositionChange({
      x: Math.round(pos.x * scaleX),
      y: Math.round(pos.y * scaleY),
    });
  };

  const renderShape = (shape: any, scale: { x: number; y: number }) => {
    const commonProps = {
      key: shape.id,
      listening: false,
    };

    switch (shape.type) {
      case 'rect':
        return (
          <Rect
            {...commonProps}
            x={shape.x / scale.x}
            y={shape.y / scale.y}
            width={(shape.width || 0) / scale.x}
            height={(shape.height || 0) / scale.y}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={1}
          />
        );
      case 'circle':
        return (
          <Circle
            {...commonProps}
            x={shape.x / scale.x}
            y={shape.y / scale.y}
            radius={(shape.radius || 0) / Math.max(scale.x, scale.y)}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={1}
          />
        );
      case 'line':
        return (
          <Line
            {...commonProps}
            points={(shape.points || []).map((p: number, i: number) => 
              i % 2 === 0 ? p / scale.x : p / scale.y
            )}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      case 'text':
        return (
          <Text
            {...commonProps}
            x={shape.x / scale.x}
            y={shape.y / scale.y}
            text={shape.text}
            fontSize={12}
            fill={shape.fill}
          />
        );
      default:
        return null;
    }
  };

  // Calculate position in canvas coordinates
  const getCanvasPosition = () => {
    if (!position || !selectedFloorPlan) return null;

    const scaleX = selectedFloorPlan.drawingData.width / canvasSize.width;
    const scaleY = selectedFloorPlan.drawingData.height / canvasSize.height;

    return {
      x: position.x / scaleX,
      y: position.y / scaleY,
    };
  };

  const canvasPosition = getCanvasPosition();

  return (
    <div className="space-y-4">
      {/* Floor Plan Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
          Terem alaprajz
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={selectedFloorPlanId || ''}
          onChange={(e) => {
            const value = e.target.value || null;
            onFloorPlanChange(value);
            if (!value) {
              onPositionChange(null);
            }
          }}
        >
          <option value="" className="dark:bg-gray-700 dark:text-gray-400">Nincs kiválasztva</option>
          {floorPlans.map((fp) => (
            <option key={fp.id} value={fp.id} className="dark:bg-gray-700">
              {fp.name}
            </option>
          ))}
        </select>
      </div>

      {/* Floor Plan Canvas */}
      {selectedFloorPlan && (
        <div ref={containerRef}>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <MapPin className="w-4 h-4 inline mr-1" />
            Kattints a gép helyére az alaprajzon
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white cursor-crosshair">
            <Stage
              ref={stageRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onClick={handleStageClick}
              onTap={handleStageClick}
            >
              <Layer>
                {/* Grid background */}
                {Array.from({ length: Math.ceil(canvasSize.width / 25) }).map((_, i) => (
                  <Line
                    key={`grid-v-${i}`}
                    points={[i * 25, 0, i * 25, canvasSize.height]}
                    stroke="#f1f5f9"
                    strokeWidth={1}
                    listening={false}
                  />
                ))}
                {Array.from({ length: Math.ceil(canvasSize.height / 25) }).map((_, i) => (
                  <Line
                    key={`grid-h-${i}`}
                    points={[0, i * 25, canvasSize.width, i * 25]}
                    stroke="#f1f5f9"
                    strokeWidth={1}
                    listening={false}
                  />
                ))}

                {/* Floor plan shapes */}
                {selectedFloorPlan.drawingData.shapes.map((shape) =>
                  renderShape(shape, {
                    x: selectedFloorPlan.drawingData.width / canvasSize.width,
                    y: selectedFloorPlan.drawingData.height / canvasSize.height,
                  })
                )}

                {/* Machine marker */}
                {canvasPosition && (
                  <>
                    {/* Outer ring */}
                    <Circle
                      x={canvasPosition.x}
                      y={canvasPosition.y}
                      radius={16}
                      fill="#3b82f6"
                      opacity={0.3}
                      listening={false}
                    />
                    {/* Inner circle */}
                    <Circle
                      x={canvasPosition.x}
                      y={canvasPosition.y}
                      radius={8}
                      fill="#3b82f6"
                      stroke="#ffffff"
                      strokeWidth={2}
                      listening={false}
                    />
                  </>
                )}
              </Layer>
            </Stage>
          </div>

          {position && (
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Pozíció: X: {position.x}, Y: {position.y}
              </p>
              <button
                type="button"
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                onClick={() => onPositionChange(null)}
              >
                Pozíció törlése
              </button>
            </div>
          )}
        </div>
      )}

      {!selectedFloorPlan && floorPlans.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Még nincs alaprajz. Hozz létre egyet a Terem alaprajz menüpontban.
        </p>
      )}
    </div>
  );
};

export default MachinePositionPicker;
